import React, { useState, useEffect } from "react";
import { Group, Popover, Text, UnstyledButton, Box, ActionIcon, MultiSelect, Checkbox, Badge, ScrollArea } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import tinycolor from "tinycolor2";

import FilterConditionSelector from "./FilterConditionSelector";

import isSpaceThemed from "../../../../../utils/IsSpaceThemed";
import isDark from "../../../../../utils/IsDarkColor";

const conditionSelectData = [
  { label: "Is", value: "is" },
  { label: "Is not", value: "is_not" },
  { label: "Is empty", value: "is_empty" },
  { label: "Is not empty", value: "is_not_empty" },
];

export default function FilterSelectInput({
  value,
  filter_id,
  options,
  isEditMode,
  selectedOptions,
  filterFormat,
  label,
  type,
  colors,
  optionCounts,
  onChange,
  onClickRemove,
  TargetComponent,
  onClearFilter,
  onUpdateFilter
}) {
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [condition, setCondition] = useState("is");
  const [newOptions, setNewOptions] = useState([]);

  const values = Array.isArray(options?.values) ? options.values : [];
  const filteredValues = values.filter((item) => String(item.name).toLowerCase().includes(search.toLowerCase()));
  const data = values.map((item) => ({ ...item, label: String(item.name) }));
  const _selectedOptions = Array.isArray(selectedOptions) ? (newOptions.length > 0 ? newOptions : selectedOptions) : [];
  const selectedValues = values.filter((item) => _selectedOptions.includes(item.value));
  const _optionCounts = Array.isArray(optionCounts) ? optionCounts.filter((o) => o.setting_id === filter_id) : [];

  const border = isSpaceThemed(colors)
    ? `1px solid ${
      isDark(colors.background_color)
        ? tinycolor(colors.background_color).lighten(20).toString()
        : tinycolor(colors.background_color).darken(20).toString()
    }`
    : `1px solid #e9ecef`;
  const inputStyles = {
    border,
    backgroundColor: isSpaceThemed(colors) ? colors.background_color : "#FFFFFF",
    color: isSpaceThemed(colors) ? colors.default_text_color : "#000000",
    "&:focus": { border }
  };

  useEffect(() => {
    if (window.$dropdownFilterId && window.$dropdownFilterId === filter_id) {
      setOpened(true);
      window.$dropdownFilterId = null;
    }
  }, []);

  const closePopover = () => {
    setOpened(false);
    window.$tiptapSidemenu = false;
  };

  const onChangeSelect = (newValue, newCondition) => {
    setNewOptions(newValue);
    if (typeof onChange === "function") {
      onChange(newValue, newCondition);
    }
  };

  const onChangeCondition = (item) => {
    setOpened(true);
    setCondition(item.value);
    onChangeSelect(["is", "is_not"].includes(item.value) ? _selectedOptions : [], item.value);
  };

  const OptionText = ({ value, label, color, isValue }) => {
    const _color = tinycolor(color).darken(60).toString();
    return (
      <Group
        spacing={0}
        px={8}
        py={2}
        mb={isValue ? 3 : 0}
        style={{
          backgroundColor: color || "#f3f3f3",
          borderRadius: 5,
          minWidth: 24,
          // textAlign: "center",
          cursor: "pointer"
        }}
        onClick={() => {
          if (isValue) {
            const _options = _selectedOptions.filter(v => v !== value);
            onChangeSelect(_options, condition);
          }
        }}
      >
        <Text size="xs" color={_color || "grey"} pr={isValue ? 5 : 0} lineClamp={1}>{label}</Text>
        {isValue && <FontAwesomeIcon icon={solid("xmark")} size="xs" color={_color || "grey"} />}
      </Group>
    )
  };

  const DropdownItem = ({ value, name, color }) => {
    const checked = _selectedOptions.includes(value);
    return (
      <Group
        noWrap
        spacing={0}
        py={5}
        px={5}
        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#f3f3f3", borderRadius: 5 } }}
        onClick={() => {
          const newOptions = checked
          ? _selectedOptions.filter(v => v !== value)
          : filterFormat === "multi_select"
          ? [..._selectedOptions, value]
          : [value];
          onChangeSelect(newOptions, condition);
        }}
      >
        {filterFormat === "multi_select" && <Checkbox checked={checked} mr={10} />}
        <OptionText value={value} label={name} color={color} />
      </Group>
    );
  };

  const onFocus = () => {
    window.$tiptapSidemenu = true;
  };

  const onBlur = () => {
    window.$tiptapCanFocus = true;
  };

  if (isEditMode && type !== "panel") {
    return (
      <MultiSelect
        placeholder="Search option..."
        value={_selectedOptions}
        data={data}
        onChange={(newValue) => onChangeSelect(newValue, condition)}
        onSearchChange={(v) => setSearch(v)}
        searchable
        withinPortal={true}
        zIndex={9999999}
        // mx={5}
        mt={5}
        rightSection={
          <Box style={{ height: "100%" }} pt={8}>
            {_selectedOptions.length > 0 && (
              <ActionIcon size="sm" onClick={onClickRemove}>
                <FontAwesomeIcon icon={solid("xmark")} size="xs" />
              </ActionIcon>
            )}
          </Box>
        }
        valueComponent={({ value, label, color }) => (
          <Group mr={5} mt={4}>
            <OptionText value={value} label={label} color={color} isValue />
          </Group>
        )}
        rightSectionWidth={_selectedOptions.length > 0 ? 28 : 0}
        itemComponent={DropdownItem}
        styles={{ input: { ...inputStyles } }}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  };

  return (
    <Popover
      opened={isEditMode ? opened : false}
      onClose={isEditMode ? closePopover : undefined}
      position="bottom"
      placement="start"
      zIndex={99999}
      spacing={0}
      target={
        TargetComponent ? (
          <TargetComponent onClick={() => setOpened(!opened)} />
        ) : (
          <UnstyledButton
            onClick={() => {
              setOpened(true);
              window.$tiptapSidemenu = true;
            }}
          >
            <Group spacing={0}>
              {selectedValues.map((selectedOption, selectedOptionKey) => {
                const _color = tinycolor(selectedOption?.color).darken(60).toString();
                return (
                  <Box
                    key={selectedOptionKey}
                    py={2}
                    px={5}
                    mr={5}
                    mb={5}
                    sx={{
                      borderRadius: 5,
                      cursor: isEditMode || type === "card" ? "pointer" : "default",
                      backgroundColor: selectedOption?.color || "#f3f3f3"
                    }}
                    onClick={type === "card" ? (e) => {
                      e.stopPropagation();
                      if (typeof onUpdateFilter === "function") {
                        onUpdateFilter([selectedOption.value], condition);
                      }
                    } : undefined}
                  >
                    <Text size="sm" color={_color || "grey"}>{selectedOption?.name}</Text>
                  </Box>
                );
              })}
              {selectedValues.length === 0 && <Text size="sm" color="grey">{"Empty"}</Text>}
            </Group>
          </UnstyledButton>
        )
      }
    >
      
      <Box style={{ backgroundColor: "#fff", borderRadius: 10 }} py={5}>
        {typeof label === "string" && (
          <FilterConditionSelector
            label={label}
            condition={condition}
            conditionData={conditionSelectData}
            onChangeCondition={onChangeCondition}
            onClearFilter={onClearFilter}
          />
        )}
        {["is", "is_not"].includes(condition) && (
          <>
            <MultiSelect
              placeholder="Search option..."
              value={_selectedOptions}
              data={data}
              onChange={(newValue) => onChangeSelect(newValue, condition)}
              onSearchChange={(value) => setSearch(value)}
              searchable
              withinPortal={true}
              zIndex={9999}
              mx={5}
              mt={5}
              sx={{ maxWidth: 300 }}
              rightSection={
                <Box style={{ height: "100%" }} pt={5}>
                  {_selectedOptions.length > 0 && (
                    <ActionIcon size="sm" onClick={onClickRemove}>
                      <FontAwesomeIcon icon={solid("xmark")} size="xs" />
                    </ActionIcon>
                  )}
                </Box>
              }
              valueComponent={({ value, label, color }) => (
                <Group mr={5}>
                  <OptionText value={value} label={label} color={color} isValue />
                </Group>
              )}
              rightSectionWidth={_selectedOptions.length > 0 ? 28 : 0}
              size="xs"
              dropdownComponent={() => <Box />}
            />
            <Box mt={5} />
            <Box style={{ maxHeight: 400, display: "flex", flex: 1 }}>
              <ScrollArea style={{ flex: 1 }}>
                {filteredValues.map((option, option_key) => {
                  const checked = _selectedOptions.includes(option.value);
                  const usage_count = _optionCounts.find(o => o.option_value === option.value)?.usage_count || 0;
                  return (
                    <Group
                      key={option_key}
                      noWrap
                      spacing={0}
                      py={5}
                      px={5}
                      mx={2}
                      sx={{ maxWidth: 300, cursor: "pointer", "&:hover": { backgroundColor: "#f3f3f3", borderRadius: 5 } }}
                      onClick={() => {
                        const newOptions = checked ?
                          _selectedOptions.filter(value => value !== option.value) :
                          filterFormat === "multi_select" ?
                            [..._selectedOptions, option.value] :
                            [option.value];
                        onChangeSelect(newOptions, condition);
                      }}
                    >
                      {filterFormat === "multi_select" && <Checkbox checked={checked} mr={10} />}
                      <OptionText value={option.value} label={option.name} color={option.color} />
                      {typeof usage_count === "number" && (
                        <>
                          <Box sx={{ flex: 1 }} />
                          <Badge
                            size="sm"
                            sx={{
                              backgroundColor: option.color,
                              color: tinycolor(option.color).darken(60).toString()
                            }}
                          >
                            {usage_count}
                          </Badge>
                        </>
                      )}
                    </Group>
                  );
                })}
              </ScrollArea>
            </Box>
          </>
        )}
      </Box>
    </Popover>
  );
}